import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Button,
    CircularProgress, Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowForwardFill from '@iconify/icons-eva/arrow-forward-fill';
import { founderSyndicationApi } from '../../utils/syndicationApi';

const STATUS_COLORS = {
    active: 'success', presentation: 'primary', qa_phase: 'info',
    commitment_phase: 'warning', allocating: 'secondary', closed: 'default',
};

const FOUNDER_NEXT_STEPS = {
    draft: { label: 'Admin is preparing the deal', color: 'text.secondary' },
    active: { label: 'Deal is live \u2014 investors being onboarded', color: 'info.main' },
    presentation: { label: 'Deal in presentation to investors', color: 'primary.main' },
    qa_phase: { label: 'Investors are asking questions \u2014 you may be asked to respond', color: 'warning.main' },
    commitment_phase: { label: 'Investors are submitting commitments', color: 'secondary.main' },
    allocating: { label: 'Allocations being finalized', color: 'info.main' },
    closed: { label: 'Deal closed \u2014 post-investment phase active', color: 'success.main' },
};

export default function FounderDealList() {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchDeals();
    }, []);

    const fetchDeals = async () => {
        try {
            const res = await founderSyndicationApi.get('founder/deals/');
            setDeals(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load deals.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>My Deals</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {deals.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary">No deals yet</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            You'll see deals here once you've been linked by an admin.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {deals.map((deal) => (
                        <Grid item xs={12} sm={6} md={4} key={deal.id}>
                            <Card sx={{
                                cursor: 'pointer', transition: 'box-shadow 0.2s',
                                '&:hover': { boxShadow: 6 }, height: '100%',
                            }}
                                onClick={() => navigate(`/founder/deals/${deal.deal_code}`)}
                            >
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                        <Typography variant="overline" color="text.secondary">
                                            {deal.deal_code}
                                        </Typography>
                                        <Chip
                                            label={deal.status?.replace(/_/g, ' ')}
                                            color={STATUS_COLORS[deal.status] || 'default'}
                                            size="small"
                                        />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }}>
                                        {deal.title || 'Untitled Deal'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {deal.company_name || '—'}
                                    </Typography>

                                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                            <Typography variant="body2" color="text.secondary">Co-investor Allocation</Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                {deal.currency_label || ''} {Number(deal.coinvestor_allocation || 0).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Instrument</Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                {(deal.instrument_type || '—').replace(/_/g, ' ')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {FOUNDER_NEXT_STEPS[deal.status] && (
                                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1.5, py: 0.75, mb: 2, borderLeft: 3, borderColor: FOUNDER_NEXT_STEPS[deal.status].color }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: FOUNDER_NEXT_STEPS[deal.status].color }}>
                                                {FOUNDER_NEXT_STEPS[deal.status].label}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ mt: 'auto' }}>
                                        <Button fullWidth variant="outlined" size="small"
                                            endIcon={<Icon icon={arrowForwardFill} />}>
                                            View Deal Room
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}
